import { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import { QuoteSaveError } from "../../shared/quoteSaveError";
import "./QuoteSaveErrorDialog.scss";

type Props = {
  error: QuoteSaveError | null;
  onClose: () => void;
};

// A save failure used to be an inline banner - easy to miss if it landed above the fold or the
// user had already scrolled past it, and it rendered the whole message (a short, actionable
// sentence PLUS the timestamp/request line/raw server payload) as one undifferentiated monospace
// block. A modal is unmissable; splitting headline from technical detail (collapsed by default,
// except when the detail itself is the fix - see quoteSaveError.ts) lets someone actually read
// what to do without wading through JSON first.
const QuoteSaveErrorDialog = ({ error, onClose }: Props) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (error) setCopied(false); }, [error]);

  const copy = () => {
    if (!error) return;
    navigator.clipboard.writeText(error.full);
    setCopied(true);
  };

  return <Dialog open={error !== null} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle className="quote-save-error-title">
      <ErrorOutlineIcon color="error" /> Quote Not Saved
    </DialogTitle>
    <DialogContent dividers>
      <Typography>{error?.headline}</Typography>
      {error && <Accordion className="quote-save-error-details" disableGutters variant="outlined" defaultExpanded={error.detailsExpandedByDefault}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="caption" color="text.secondary">Technical details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="pre" className="quote-save-error-pre">{error.details}</Box>
        </AccordionDetails>
      </Accordion>}
    </DialogContent>
    <DialogActions className="quote-save-error-actions">
      <Button color="inherit" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copy}>{copied ? "Copied" : "Copy Error"}</Button>
      <Button variant="contained" onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>;
};

export default QuoteSaveErrorDialog;
